import { Card, SimpleGrid, Stack, Text, Title } from '@mantine/core'
import { UseFormReturnType } from '@mantine/form'
import { ReactNode } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { PlatformAlert } from '@/components/ui-shared/PlatformAlert/PlatformAlert'
import { useGetMilestoneServerList } from '@/queries/vlmQueries'
import { MAX_ITEMS_WITHOUT_PAGINATION } from '@/utils/pagination'
import { PipelineFormValues } from '../VlmPipelineForm'
import { getPatrolTimeValues } from '../pipelineSourceValues'
import {
  formatDuration,
  formatPatrolInterval,
  formatPatrolIntervalFrom,
  formatPatrolSchedule,
  roundTimeoutToSeconds
} from './patrolSchedule'

type SummaryItemProps = {
  label: ReactNode
  value: ReactNode
}

const SummaryItem = ({ label, value }: SummaryItemProps) => (
  <Stack gap={2}>
    <Text size="xs" c="dimmed">
      {label}
    </Text>
    <Text size="sm" fw={500}>
      {value}
    </Text>
  </Stack>
)

type PatrolSummaryCardProps = {
  form: UseFormReturnType<PipelineFormValues>
}

export const PatrolSummaryCard = ({ form }: PatrolSummaryCardProps) => {
  const intl = useIntl()
  const { source, actions } = form.values

  const isRegistered = source.patrolVideoSource === 'rtsp'

  // Already cached by the server picker on the source step.
  const { data } = useGetMilestoneServerList(
    { limit: MAX_ITEMS_WITHOUT_PAGINATION },
    !isRegistered
  )
  const serverName =
    data?.results.find((server) => server.id === source.patrolServerId)?.name ??
    '—'

  const schedule = source.patrolLegacyEvery
    ? formatPatrolInterval(source.patrolInterval, intl)
    : source.patrolScheduleMode === 'every'
      ? formatPatrolIntervalFrom(
          source.patrolInterval,
          source.patrolStartTime,
          source.patrolTimezone,
          intl
        )
      : formatPatrolSchedule(
          {
            mode: 'at',
            at: getPatrolTimeValues(source.patrolTimes),
            timezone: source.patrolTimezone
          },
          intl
        )

  const deliversSummary = actions.some(
    (action) =>
      action.type === 'email' && action.alert_tags.includes('patrol_summary')
  )

  return (
    <Card padding="lg" withBorder>
      <Stack gap="md">
        <Title order={5} fz="sm">
          <FormattedMessage id="vlm.pipelines.create.patrol.summary.title" />
        </Title>

        <SimpleGrid cols={{ base: 2, sm: 3 }}>
          {isRegistered ? (
            <SummaryItem
              label={
                <FormattedMessage id="vlm.pipelines.create.patrol.videoSource.label" />
              }
              value={
                <FormattedMessage id="vlm.pipelines.create.patrol.videoSource.rtsp" />
              }
            />
          ) : (
            <SummaryItem
              label={
                <FormattedMessage id="vlm.pipelines.create.milestone.label" />
              }
              value={<span>{serverName}</span>}
            />
          )}
          <SummaryItem
            label={<FormattedMessage id="vlm.pipelines.create.cameras.label" />}
            value={
              <FormattedMessage
                id="vlm.pipelines.create.patrol.summary.cameras"
                values={{ count: source.patrolCameras.length }}
              />
            }
          />
          <SummaryItem
            label={<FormattedMessage id="vlm.pipelines.create.step.schedule" />}
            value={<span>{schedule}</span>}
          />
          <SummaryItem
            label={
              <FormattedMessage id="vlm.pipelines.create.patrol.schedule.clip.label" />
            }
            value={<span>{`${source.patrolClipSeconds}s`}</span>}
          />
          <SummaryItem
            label={
              <FormattedMessage id="vlm.pipelines.create.patrol.schedule.timeout.label" />
            }
            value={
              <span>
                {formatDuration(
                  roundTimeoutToSeconds(source.patrolRoundTimeoutMinutes),
                  intl
                )}
              </span>
            }
          />
          <SummaryItem
            label={<FormattedMessage id="vlm.pipelines.create.step.actions" />}
            value={
              <FormattedMessage
                id="vlm.pipelines.create.patrol.summary.actions"
                values={{ count: actions.length }}
              />
            }
          />
        </SimpleGrid>

        {!deliversSummary && (
          <PlatformAlert variant="warning" compact>
            <FormattedMessage id="vlm.pipelines.create.patrol.summary.noDelivery" />
          </PlatformAlert>
        )}
      </Stack>
    </Card>
  )
}
